import { Alert, CircularProgress } from '@mui/material';
import React from 'react';

import { useTranslate } from '@/hooks/useTranslate';

import type {
  CameraStatus,
  DocumentScannerProps,
} from './documentScanner.types';
import { useDocumentScanner } from './useDocumentScanner';

import {
  BackButton,
  CameraSection,
  CaptureActions,
  CaptureNowButton,
  ConfirmButton,
  RetakeButton,
  ReviewActions,
  ReviewBackButton,
  ReviewContainer,
  ReviewImage,
  ReviewImageWrapper,
  ReviewPage,
  ReviewTitle,
  ScannerContainer,
  StatusContent,
  StatusGuideFrame,
  StatusIndicator,
  StatusMessage,
  StatusOverlay,
  StyledOverlay,
  StyledVideo,
} from './DocumentScanner.styled';

/** Statuses that show a loading spinner (vs. a static error icon) */
const LOADING_STATUSES: ReadonlySet<CameraStatus> = new Set([
  'pending',
  'requesting',
  'loading-libs',
]);

const STATUS_KEYS: Record<Exclude<CameraStatus, 'active'>, string> = {
  pending: 'documentScanner.status.pending',
  requesting: 'documentScanner.status.requesting',
  'loading-libs': 'documentScanner.status.loadingLibs',
  denied: 'documentScanner.status.denied',
  unavailable: 'documentScanner.status.unavailable',
};

export const DocumentScanner: React.FC<DocumentScannerProps> = props => {
  const { t } = useTranslate();

  const {
    step,
    cameraStatus,
    capturedImageUrl,
    isSubmitting,
    videoRef,
    overlayRef,
    captureNow,
    retake,
    confirm,
  } = useDocumentScanner({
    ...props,
    guideLabel: t('documentScanner.guideLabel'),
  });

  if (step === 'review' && capturedImageUrl) {
    return (
      <ReviewPage>
        {props.onBack && (
          <ReviewBackButton type="button" onClick={props.onBack}>
            {t('documentScanner.button.back')}
          </ReviewBackButton>
        )}
        <ReviewContainer>
          <ReviewTitle>{t('documentScanner.review.title')}</ReviewTitle>
          <ReviewImageWrapper>
            <ReviewImage src={capturedImageUrl} alt="Captured document" />
          </ReviewImageWrapper>
          {props.error && <Alert severity="error">{props.error}</Alert>}
          <ReviewActions>
            <RetakeButton
              type="button"
              onClick={() => {
                retake();
                props.onRetake?.();
              }}
              disabled={isSubmitting}
            >
              {t('documentScanner.review.button.retake')}
            </RetakeButton>
            <ConfirmButton
              type="button"
              onClick={confirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                t('documentScanner.review.button.confirm')
              )}
            </ConfirmButton>
          </ReviewActions>
        </ReviewContainer>
      </ReviewPage>
    );
  }

  const statusKey = cameraStatus !== 'active' ? cameraStatus : null;
  const isLoading = statusKey !== null && LOADING_STATUSES.has(statusKey);
  const backButton = props.onBack ? (
    <BackButton type="button" onClick={props.onBack}>
      {t('documentScanner.button.back')}
    </BackButton>
  ) : null;

  return (
    <ScannerContainer>
      <CameraSection>
        <StyledVideo
          ref={videoRef as React.Ref<HTMLVideoElement>}
          autoPlay
          playsInline
          muted
        />
        <StyledOverlay ref={overlayRef as React.Ref<HTMLCanvasElement>} />
        {backButton}

        <StatusOverlay $visible={statusKey !== null}>
          {statusKey && (
            <>
              <StatusContent>
                <StatusGuideFrame>
                  <StatusIndicator>
                    {isLoading ? (
                      <CircularProgress
                        size={36}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      />
                    ) : (
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="rgba(255,255,255,0.7)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    )}
                  </StatusIndicator>
                  <StatusMessage>{t(STATUS_KEYS[statusKey])}</StatusMessage>
                </StatusGuideFrame>
              </StatusContent>
            </>
          )}
        </StatusOverlay>

        {cameraStatus === 'active' && (
          <CaptureActions>
            <CaptureNowButton type="button" onClick={captureNow}>
              {t('documentScanner.button.captureNow')}
            </CaptureNowButton>
          </CaptureActions>
        )}
      </CameraSection>
    </ScannerContainer>
  );
};
